import { z } from 'zod';

// Company Profile API DTOs (snake_case wire contract). A Company Profile is the
// org's own GTM context (the `org_profile.*` injected into agents): attributes
// live on the linked `companies` row; this is the org-owned strategy/ICP overlay.
// See packages/db/src/schema/company-profiles.ts and the Actions & Integrations
// IA v2 plan.

const icpSegmentSchema = z.object({
  name: z.string(),
  description: z.string().optional(),
});
export type IcpSegmentDto = z.infer<typeof icpSegmentSchema>;

// A persona the profile targets, resolved to its display label.
export const personaRefSchema = z.object({
  key: z.string(),
  name: z.string(),
});
export type PersonaRef = z.infer<typeof personaRefSchema>;

// A curated company link. Label optional — the UI derives one from the host
// (linkedin.com → "LinkedIn") when absent; icons always derive from the host.
export const companyLinkSchema = z.object({
  url: z.string().min(1).max(512),
  label: z.string().max(60).optional(),
});
export type CompanyLink = z.infer<typeof companyLinkSchema>;

// 'self' = the org's own GTM profile (the injected org_profile context);
// 'account' = a Target Account — living insights on an account the org sells to.
export const companyProfileKindSchema = z.enum(['self', 'account']);
export type CompanyProfileKind = z.infer<typeof companyProfileKindSchema>;

// Who buys and who sells internally (concord-gtm icp.md structure).
export const buyingCommitteeSchema = z.object({
  economic_buyer: z.string().max(255).optional(),
  champions: z.array(z.string().max(255)).max(20).optional(),
  influencers: z.array(z.string().max(255)).max(20).optional(),
});
export type BuyingCommittee = z.infer<typeof buyingCommitteeSchema>;

// The prioritization the Prospect Researcher assigns a brief.
export const prospectPrioritizationSchema = z.enum(['lean_in', 'wait', 'pass']);
export type ProspectPrioritization = z.infer<
  typeof prospectPrioritizationSchema
>;

// A Target Account's CURRENT research state — latest-only by design (run
// history lives in action_runs). `brief`/`fit` store the runs' JSON output
// loosely so the action contracts can grow without breaking stored insights.
// PATCH semantics: insights updates MERGE shallowly server-side (`||`) — send
// only the keys you own (e.g. `{ watch_opt_out }`), never a full stale copy.
export const companyProfileInsightsSchema = z.object({
  // Latest Prospect Research brief, verbatim run output.
  brief: z.record(z.string(), z.unknown()).optional(),
  // When `brief` was written. Account Watch also bumps `last_refreshed_at`.
  brief_refreshed_at: z.string().optional(),
  prioritization: prospectPrioritizationSchema.optional(),
  // Latest Lead Qualification result (fit score, verdict, reasons).
  fit: z.record(z.string(), z.unknown()).optional(),
  last_refreshed_at: z.string().optional(),
  // Account Watch's OWN keys — kept disjoint from `brief` (which Prospect
  // Research owns) so the shallow server-side merge makes concurrent writers
  // key-safe with no read-modify-write.
  watch_summary: z.string().optional(),
  watch_positioning: z.string().optional(),
  // When Account Watch last checked; the next check searches from a week before.
  watch_checked_at: z.string().optional(),
  // This account's own Watch choice (true = off); unset follows the Watch.
  watch_opt_out: z.boolean().optional(),
});
export type CompanyProfileInsights = z.infer<typeof companyProfileInsightsSchema>;

// A brief's positioning as a Target Account one-liner: short text stays whole,
// long text keeps its first sentence so the account card stays scannable.
export const firstSentence = (text: string): string => {
  const trimmed = text.trim();
  if (trimmed.length <= 160) return trimmed;
  const sentence = trimmed.match(/^(.*?[.!?])(\s|$)/)?.[1];
  return sentence ?? trimmed.slice(0, 160);
};

// Unset accounts follow the Watch's `watch_all_accounts` option, on by default.
export const isAccountWatched = (
  insights: CompanyProfileInsights | null | undefined,
  watchOptions: Record<string, unknown> | null | undefined,
): boolean =>
  typeof insights?.watch_opt_out === 'boolean'
    ? !insights.watch_opt_out
    : watchOptions?.watch_all_accounts !== false;

export const profileLiveRunSchema = z.object({
  id: z.string(),
  action_key: z.string(),
  started_at: z.string(),
});
export type ProfileLiveRun = z.infer<typeof profileLiveRunSchema>;

export const companyProfileSchema = z.object({
  id: z.string(),
  // Linked attribute company row; null until the org's own company resolves.
  company_id: z.string().nullable(),
  // Attributes surfaced from the linked company (read-only here).
  domain: z.string().nullable(),
  attributes: z.record(z.string(), z.unknown()),
  // When the linked company was last enriched (ISO); null when never enriched
  // (or no linked company). Powers the account page's "Enriched {date}" stamp.
  enriched_at: z.string().nullable(),
  kind: z.string(), // 'self' | 'account' (see companyProfileKindSchema)
  is_default: z.boolean(),
  // Target Accounts only; null for self-kind and never-researched accounts.
  insights: companyProfileInsightsSchema.nullable(),
  // Org-curated links on the LINKED COMPANY (read-only here; edited via the
  // patch's `links` write-through). Labels/icons derive from the host.
  links: z.array(companyLinkSchema),
  label: z.string(),
  one_liner: z.string().nullable(),
  value_props: z.array(z.string()),
  product_lines: z.array(z.string()),
  competitors: z.array(z.string()),
  target_markets: z.array(z.string()),
  icp_narrative: z.string().nullable(),
  icp_segments: z.array(icpSegmentSchema),
  geo_focus: z.array(z.string()),
  // ICP extras: outbound-worthy signal taxonomy, explicit disqualifiers, and
  // the buying committee (concord-gtm icp.md structure).
  trigger_events: z.array(z.string()),
  anti_icp: z.array(z.string()),
  buying_committee: buyingCommitteeSchema,
  personas: z.array(personaRefSchema),
  // Agent keys that last wrote this profile, e.g. ['profile.build','icp.analyze'].
  built_by: z.array(z.string()),
  source: z.string(), // 'agent' | 'manual'
  updated_at: z.string(),
  // Admin reads only, so the page can poll while a run is out.
  live_run: profileLiveRunSchema.nullable().optional(),
});
export type CompanyProfile = z.infer<typeof companyProfileSchema>;

export const companyProfilesListResponseSchema = z.object({
  profiles: z.array(companyProfileSchema),
  default_profile_id: z.string().nullable(),
});
export type CompanyProfilesListResponse = z.infer<
  typeof companyProfilesListResponseSchema
>;

// Create: only `label` is required; the rest is filled by the agent or by hand.
// `company_domain` optionally links/creates the attribute company row.
export const createCompanyProfileRequestSchema = z.object({
  label: z.string().min(1).max(160),
  // Omitted = 'self'. 'account' creates a Target Account (never org default).
  kind: companyProfileKindSchema.optional(),
  // Target Accounts: latest research state (written by Save as Target Account
  // and the Watch/refresh pipeline; watch_opt_out toggled from the insights UI).
  insights: companyProfileInsightsSchema.optional(),
  company_domain: z.string().max(255).optional(),
  one_liner: z.string().max(2000).optional(),
  value_props: z.array(z.string().max(500)).max(50).optional(),
  product_lines: z.array(z.string().max(255)).max(50).optional(),
  competitors: z.array(z.string().max(255)).max(50).optional(),
  target_markets: z.array(z.string().max(255)).max(50).optional(),
  icp_narrative: z.string().max(8000).optional(),
  icp_segments: z.array(icpSegmentSchema).max(20).optional(),
  geo_focus: z.array(z.string().max(120)).max(50).optional(),
  trigger_events: z.array(z.string().max(255)).max(50).optional(),
  anti_icp: z.array(z.string().max(255)).max(50).optional(),
  buying_committee: buyingCommitteeSchema.optional(),
  persona_keys: z.array(z.string().max(64)).max(50).optional(),
  // Merged into the profile's attributes jsonb (org-owned overlay fields the
  // edit screen exposes; distinct from enrichment-owned company firmographics).
  industry: z.string().max(255).optional(),
  linkedin_url: z.string().max(512).optional(),
  twitter_url: z.string().max(512).optional(),
  // Full-replace of the linked company's curated links (cap 10).
  links: z.array(companyLinkSchema).max(10).optional(),
  // Set true to make this the org's default profile on create.
  is_default: z.boolean().optional(),
});
export type CreateCompanyProfileRequest = z.infer<
  typeof createCompanyProfileRequestSchema
>;

// Run the Profile Agent (`profile.build`) from a website to create or refresh a
// profile. `mode='refresh'` requires `profile_id`.
export const buildCompanyProfileRequestSchema = z.object({
  website: z.string().min(1).max(512),
  // Identity facts — written through to the linked company's user-attribute
  // overlay (created-or-linked by domain).
  linkedin_url: z.string().max(512).optional(),
  twitter_url: z.string().max(512).optional(),
  // Extra research URLs (G2, Crunchbase, docs ...) — appended to the linked
  // company's curated links (cap 10).
  source_urls: z.array(z.string().max(512)).max(5).optional(),
  mode: z.enum(['create', 'refresh']).default('create'),
  profile_id: z.string().optional(),
  // Refresh-only merge policy (ignored on create, which always writes fresh).
  //   omitted / false → FILL GAPS: the agent writes ONLY fields that are
  //     currently empty on the profile; every non-empty field (whether the user
  //     or a prior agent run authored it) is kept verbatim.
  //   true → FULL OVERWRITE: every researched field is replaced (legacy).
  overwrite: z.boolean().optional(),
});
export type BuildCompanyProfileRequest = z.infer<
  typeof buildCompanyProfileRequestSchema
>;

// Patch: every field optional; only provided keys are written. Named `patch*`
// (not `update*`) to avoid colliding with companies.ts's attributes-edit
// `updateCompanyProfileRequestSchema` — that one edits an enriched *account*'s
// attributes; this one edits the org's own GTM overlay. (The two converge
// under the "Company Profile" concept; unifying the names is deferred to the
// Accounts rename.)
export const patchCompanyProfileRequestSchema =
  createCompanyProfileRequestSchema.partial();
export type PatchCompanyProfileRequest = z.infer<
  typeof patchCompanyProfileRequestSchema
>;
